import { useQuery } from '@tanstack/react-query'
import { Car, ChevronRight, ExternalLink, LogOut, Plus, Star, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { driver, useAction, useActive } from '@/api/driver'
import type { NotificationPrefs, User, Vehicle } from '@/api/types'
import { PageTitle } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Field, Input, Select, Switch } from '@/components/ui/form'
import { ConfirmDialog, Dialog } from '@/components/ui/overlay'
import { useAuth } from '@/lib/auth'
import { date } from '@/lib/format'
import { useI18n } from '@/lib/i18n'
import { useTheme } from '@/lib/theme'
import { cn } from '@/lib/utils'

export function AccountPage() {
  const { t } = useI18n()
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  if (!user) return null
  return (
    <div>
      <PageTitle title={user.full_name || t('Your account')} sub={[user.phone, user.email].filter(Boolean).join(' · ')} />
      <div className="space-y-4">
        <ProfileCard user={user} />
        <VehiclesCard />
        <NotificationsCard />
        <PreferencesCard />
        <Card className="divide-y divide-border">
          <Link to="/help" className="flex items-center justify-between px-4 py-3 text-sm hover:bg-surface-2">{t('Help and support')} <ChevronRight className="size-4 text-muted" /></Link>
          <a href="https://saadhan.app/privacy" target="_blank" rel="noreferrer" className="flex items-center justify-between px-4 py-3 text-sm hover:bg-surface-2">{t('Privacy policy')} <ExternalLink className="size-4 text-muted" /></a>
          <a href="https://saadhan.app/terms" target="_blank" rel="noreferrer" className="flex items-center justify-between px-4 py-3 text-sm hover:bg-surface-2">{t('Terms of use')} <ExternalLink className="size-4 text-muted" /></a>
        </Card>
        <Button variant="outline" className="w-full" onClick={async () => { await logout(); navigate('/') }}><LogOut /> {t('Sign out')}</Button>
        {user.role === 'user' && <CloseAccount />}
        <p className="text-center text-xs text-muted">{t('Member since {d}', { d: date(user.created_at) })}</p>
      </div>
    </div>
  )
}

function ProfileCard({ user }: { user: User }) {
  const { t } = useI18n()
  const { refreshUser } = useAuth()
  const [name, setName] = useState(user.full_name ?? '')
  const [email, setEmail] = useState(user.email ?? '')
  const dirty = name !== (user.full_name ?? '') || email !== (user.email ?? '')
  const save = useAction(() => driver.updateProfile({ full_name: name.trim() || null, email: email.trim() || null }), { success: t('Saved'), onSuccess: () => refreshUser() })
  return (
    <Card className="p-4">
      <p className="mb-3 font-semibold">{t('Your details')}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('Full name')}>{(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />}</Field>
        <Field label={t('Email (optional)')}>{(p) => <Input {...p} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />}</Field>
      </div>
      <p className="mt-2 text-xs text-text-2">{t('Your phone number signs you in and cannot be changed here.')}</p>
      {dirty && <Button className="mt-3" loading={save.isPending} onClick={() => save.mutate(undefined)}>{t('Save')}</Button>}
    </Card>
  )
}

function VehiclesCard() {
  const { t } = useI18n()
  const q = useQuery({ queryKey: ['vehicles'], queryFn: driver.vehicles })
  const [editing, setEditing] = useState<Vehicle | 'new' | null>(null)
  const remove = useAction((v: Vehicle) => driver.deleteVehicle(v.id), { success: t('Vehicle removed'), invalidate: [['vehicles']] })
  const primary = useAction((v: Vehicle) => driver.updateVehicle(v.id, { is_primary: true }), { invalidate: [['vehicles']] })
  return (
    <Card className="p-4">
      <div className="mb-2 flex items-center justify-between">
        <p className="font-semibold">{t('Your vehicles')}</p>
        <Button size="sm" variant="ghost" onClick={() => setEditing('new')}><Plus /> {t('Add')}</Button>
      </div>
      {q.data?.vehicles.length ? (
        <div className="divide-y divide-border">
          {q.data.vehicles.map((v) => (
            <div key={v.id} className="flex items-center gap-3 py-2.5">
              <Car className="size-5 text-text-2" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{v.brand} {v.model} {v.variant ?? ''}</p>
                <p className="text-xs text-text-2">{v.battery_kwh ? `${v.battery_kwh} kWh` : ''}{v.is_primary ? ` · ${t('Main vehicle')}` : ''}</p>
              </div>
              {!v.is_primary && <Button size="icon-sm" variant="ghost" aria-label={t('Make main vehicle')} onClick={() => primary.mutate(v)}><Star /></Button>}
              <Button size="icon-sm" variant="ghost" aria-label={t('Remove')} onClick={() => remove.mutate(v)}><Trash2 /></Button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-text-2">{q.isLoading ? '…' : t('Add your car to see how far a charge gets you.')}</p>
      )}
      {editing && <VehicleDialog onClose={() => setEditing(null)} first={!q.data?.vehicles.length} />}
    </Card>
  )
}

function VehicleDialog({ onClose, first }: { onClose: () => void; first: boolean }) {
  const { t } = useI18n()
  const catalog = useQuery({ queryKey: ['catalog'], queryFn: driver.catalog, staleTime: Infinity })
  const [brand, setBrand] = useState('')
  const [model, setModel] = useState('')
  const [battery, setBattery] = useState('')
  const list = catalog.data?.vehicles ?? []
  const brands = [...new Set(list.map((v) => v.brand))].sort()
  const models = list.filter((v) => v.brand === brand)
  const save = useAction(() => {
    const [m, variant] = model.split('||')
    return driver.addVehicle({ brand: brand.trim(), model: (m ?? '').trim(), variant: variant || null, battery_kwh: battery ? Number(battery) : null, is_primary: first })
  }, { success: t('Vehicle added'), invalidate: [['vehicles']], onSuccess: onClose })
  const fromCatalog = brands.includes(brand)
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={t('Add a vehicle')} footer={<><Button variant="outline" onClick={onClose}>{t('Cancel')}</Button><Button loading={save.isPending} disabled={!brand.trim() || !model.trim()} onClick={() => save.mutate(undefined)}>{t('Add')}</Button></>}>
      <div className="grid gap-3">
        <Field label={t('Brand')}>
          {(p) => (
            <>
              <Input {...p} list="brands" value={brand} onChange={(e) => { setBrand(e.target.value); setModel('') }} placeholder="BYD, Tata, MG…" />
              <datalist id="brands">{brands.map((b) => <option key={b} value={b} />)}</datalist>
            </>
          )}
        </Field>
        <Field label={t('Model')}>
          {(p) =>
            fromCatalog ? (
              <Select {...p} value={model} onChange={(e) => { setModel(e.target.value); const v = models.find((x) => `${x.model}||${x.variant ?? ''}` === e.target.value); setBattery(v?.battery_kwh ? String(v.battery_kwh) : '') }}>
                <option value="">{t('Choose')}</option>
                {models.map((v) => <option key={`${v.model}${v.variant}`} value={`${v.model}||${v.variant ?? ''}`}>{v.model}{v.variant ? ` ${v.variant}` : ''}</option>)}
              </Select>
            ) : (
              <Input {...p} value={model} onChange={(e) => setModel(e.target.value)} />
            )
          }
        </Field>
        <Field label={t('Battery (kWh, optional)')}>{(p) => <Input {...p} type="number" min={0} value={battery} onChange={(e) => setBattery(e.target.value)} />}</Field>
      </div>
    </Dialog>
  )
}

const PREFS: { key: keyof NotificationPrefs; label: string }[] = [
  { key: 'charging_started', label: 'Charging started' },
  { key: 'charging_completed', label: 'Charging finished' },
  { key: 'charging_interrupted', label: 'Charging interrupted' },
  { key: 'balance_low', label: 'Low balance' },
  { key: 'topup_credited', label: 'Credit added' },
]

function NotificationsCard() {
  const { t } = useI18n()
  const q = useQuery({ queryKey: ['prefs'], queryFn: driver.notificationPrefs })
  const save = useAction((p: NotificationPrefs) => driver.setNotificationPrefs(p), { invalidate: [['prefs']] })
  if (!q.data) return null
  return (
    <Card className="p-4">
      <p className="font-semibold">{t('Phone notifications')}</p>
      <p className="mb-2 text-xs text-text-2">{t('Sent to the Saadhan Charge app if you also have it installed.')}</p>
      <div className="divide-y divide-border">
        {PREFS.map((p) => (
          <label key={p.key} className="flex items-center justify-between py-2.5 text-sm">
            {t(p.label)}
            <Switch checked={q.data[p.key]} label={t(p.label)} onChange={(v) => save.mutate({ ...q.data, [p.key]: v })} />
          </label>
        ))}
      </div>
    </Card>
  )
}

function PreferencesCard() {
  const { t, lang, setLang } = useI18n()
  const { theme, setTheme } = useTheme()
  const opt = (on: boolean) => cn('flex-1 rounded-lg border px-3 py-2 text-sm', on ? 'border-brand bg-brand-soft font-medium' : 'border-border hover:bg-surface-2')
  return (
    <Card className="space-y-4 p-4">
      <div>
        <p className="mb-2 text-sm font-semibold">{t('Language')}</p>
        <div className="flex gap-2">
          <button className={opt(lang === 'en')} onClick={() => setLang('en')}>English</button>
          <button className={opt(lang === 'ne')} onClick={() => setLang('ne')}>नेपाली</button>
        </div>
      </div>
      <div>
        <p className="mb-2 text-sm font-semibold">{t('Appearance')}</p>
        <div className="flex gap-2">
          <button className={opt(theme === 'light')} onClick={() => setTheme('light')}>{t('Light')}</button>
          <button className={opt(theme === 'dark')} onClick={() => setTheme('dark')}>{t('Dark')}</button>
          <button className={opt(theme === 'system')} onClick={() => setTheme('system')}>{t('System')}</button>
        </div>
      </div>
    </Card>
  )
}

function CloseAccount() {
  const { t } = useI18n()
  const { logout } = useAuth()
  const navigate = useNavigate()
  const active = useActive()
  const [open, setOpen] = useState(false)
  const charging = (active.data?.active_sessions.length ?? 0) > 0
  return (
    <>
      <button className="w-full text-center text-sm text-danger underline-offset-4 hover:underline disabled:opacity-50" disabled={charging} onClick={() => setOpen(true)}>
        {charging ? t('Stop charging before closing your account') : t('Close my account')}
      </button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={t('Close your account?')}
        body={t('You are signed out everywhere and can no longer charge with your cards. Any wallet balance stays with the operators; contact them about it first.')}
        confirmLabel={t('Close account')}
        danger
        onConfirm={async () => {
          await driver.closeAccount()
          await logout()
          navigate('/')
        }}
      />
    </>
  )
}
